import React, { useMemo, useRef } from 'react';

import { Button, Text, Title } from '@soluto-design/react';

import cls from '@soluto-design/styles/cls';

import IframeForm from '../../../../molecules/Form';
import Steps from '../../../../molecules/Steps';
import Content from '../Content';
import Form from '../Form';

interface ContentStepsProps {
    step: number;
    title: string;
    description: string;
    caption: string;
    benefits: {
        title: string;
        description: string;
        icon: string;
    }[];
    calculatorTitle: string;
    calculatorDescription: string;
    marker: {
        latitude: number;
        longitude: number;
    };
    onSubmit: (data: any) => void;
    isSubmitted: boolean;
    formTitle: string;
    formDescription: string;
    submitTitle: string;
    submitDescription: string;
    form: {
        title: string;
        description: string;
        fields: {
            name: string;
            label: string;
            type: string;
            required: boolean;
            placeholder: string;
        }[];
        button: {
            label: string;
        };
        action: string;
        method: string;
    };
    onSubmitted: (data: any) => void;
    backButton: {
        href: string;
        target: string;
        label: string;
        children: string;
    };
    resultCalculator: {
        [key: string]: string;
    };
    onClickStepBack: (step: number) => void;
    labelSteps: string;
    translations: {
        [key: string]: {
            text: string;
        };
    };
}

const ContentSteps = ({
    step,
    title,
    description,
    caption,
    benefits,
    calculatorTitle,
    calculatorDescription,
    marker,
    onSubmit,
    isSubmitted,
    formTitle,
    formDescription,
    submitTitle,
    submitDescription,
    form,
    onSubmitted,
    backButton,
    resultCalculator,
    onClickStepBack,
    labelSteps,
    translations,
}: ContentStepsProps) => {
    let displayInput = useRef<boolean>(true);

    const [pre, post] = useMemo(() => {
        let preContent: React.ReactNode = null;
        let postContent: React.ReactNode = null;

        if (step === 0) {
            displayInput.current = true;

            preContent = (
                <>
                    <Title
                        className="!text-h300 text-text-primary mb-12"
                        component="h1">
                        {title}
                    </Title>
                    <Text
                        component="div"
                        isHtml
                        className="text-b300 text-text-primary mb-xg">
                        {description}
                    </Text>
                </>
            );
            postContent = (
                <>
                    <Text
                        component="div"
                        isHtml
                        className={cls(
                            'text-c100 text-text-secondary mt-16 text-end [&_a]:!text-text-secondary [&_a]:!no-underline [&_a]:!font-regular [&_a]:hover:!text-text-primary',
                        )}>
                        {caption}
                    </Text>
                    <div
                        className={cls(
                            'h-[1px] bg-stroke-secondary mt-xg rounded-large',
                        )}
                    />
                    <div
                        className={cls(
                            'grid grid-cols-12 gap-y-xg mt-xg overflow-hidden relative lg:gap-x-xg',
                        )}>
                        {benefits?.length > 0 &&
                            benefits.map((benefit) => {
                                return (
                                    <div
                                        key={`benefit-${benefit?.title}-${benefit?.description}`}
                                        className="col-span-12 lg:col-span-4">
                                        <Content
                                            title={benefit.title}
                                            description={benefit.description}
                                            icon={benefit.icon}
                                        />
                                    </div>
                                );
                            })}
                    </div>
                </>
            );

            return [preContent, postContent];
        }
        if (step === 1) {
            displayInput.current = true;

            preContent = (
                <Title
                    className="!text-h300 text-text-primary mb-xg"
                    component="h2">
                    {calculatorTitle}
                </Title>
            );
            postContent = (
                <>
                    <Text
                        component="div"
                        isHtml
                        className="text-b300 text-text-primary my-xg">
                        {calculatorDescription}
                    </Text>
                    <Form
                        translations={translations}
                        location={{
                            latitude: marker?.latitude,
                            longitude: marker?.longitude,
                        }}
                        onSubmit={onSubmit}
                        className={cls('')}
                    />
                </>
            );

            return [preContent, postContent];
        }

        if (step === 2) {
            displayInput.current = false;
            preContent = !isSubmitted ? (
                <>
                    <Title
                        className="!text-h300 text-text-primary mb-12"
                        component="h2">
                        {formTitle}
                    </Title>
                    <Text
                        component="div"
                        isHtml
                        className="text-b300 text-text-primary">
                        {formDescription}
                    </Text>
                </>
            ) : (
                <>
                    <Title
                        className="!text-h300 text-text-primary mb-12"
                        component="h2">
                        {submitTitle}
                    </Title>
                    <Text
                        component="div"
                        isHtml
                        className="text-b300 text-text-primary submitted-message">
                        {submitDescription}
                    </Text>
                </>
            );
            postContent = !isSubmitted ? (
                <IframeForm
                    {...form}
                    className="mt-xg"
                    onSubmit={onSubmitted}
                    onReady={(node) => {
                        let input =
                            document.getElementsByName('lcoe_calculator')[0];

                        if (node?.firstChild?.contentWindow) {
                            input =
                                node.firstChild.contentWindow.document.getElementsByName(
                                    'lcoe_calculator',
                                )[0];
                        }

                        if (!input) {
                            return;
                        }

                        (input as HTMLInputElement).value = new URLSearchParams(
                            resultCalculator,
                        ).toString();
                    }}
                />
            ) : (
                <div className="flex justify-start mt-xg">
                    <Button
                        href={backButton?.href}
                        target={backButton?.target}
                        variant="primary"
                        size="large">
                        {backButton?.label ?? backButton?.children}
                    </Button>
                </div>
            );

            return [preContent, postContent];
        }

        return [preContent, postContent];
    }, [
        backButton,
        benefits,
        calculatorDescription,
        calculatorTitle,
        caption,
        description,
        form,
        formDescription,
        formTitle,
        isSubmitted,
        marker,
        onSubmitted,
        resultCalculator,
        step,
        submitDescription,
        submitTitle,
        onSubmit,
        title,
        translations,
    ]);

    return (
        <>
            {step <= 2 && !isSubmitted && (
                <Steps
                    className="pb-lg"
                    text={labelSteps}
                    current={step}
                    onClick={() => {
                        onClickStepBack(step);
                    }}
                    total={2}
                />
            )}
            {pre}
            <div
                id="geocoder"
                className={cls('')}
                style={{
                    display: displayInput.current ? 'block' : 'none',
                }}
            />
            {post}
        </>
    );
};

export default ContentSteps;
